import { DefaultSeo } from 'next-seo';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { ReactQueryConfigProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query-devtools';

import ShopperGlobalStyles from 'shopper/components/GlobalStyles';

import DownloadAppCTA from 'components/DownloadAppCTA';
import DrawerRoot from 'components/Drawer/Root';
import GlobalStyles from 'components/GlobalStyles';
import GoogleTagManager from 'components/GoogleTagManager';
import LGPDDialogBox from 'components/LGPDDialogBox';
import ModalRoot from 'components/ModalRoot';
import MouseflowScriptTag from 'components/MouseflowScriptTag';
import ToastRoot from 'components/Toast/Root';
import Topbar from 'components/Topbar';
import UniversalSchemas from 'components/UniversalSchemas';
import WishlistModalOnUrlHash from 'components/Wishlist/WishlistModal/WishlistModalOnUrlHash';

import { sendPageView, sendWebVitalsToGoogleAnalytics } from 'lib/gtag';
import {
  clearStoragesWebPushPermissionEntries,
  initializeFirebaseApp,
  isNotificationOnboardingShowed,
} from 'lib/pushNotifications';
import { get } from 'lib/request';
import { parseToBool } from 'lib/string';

import DrawerProvider from 'providers/DrawerProvider';
import LocalWishlistProvider from 'providers/LocalWishlistProvider';
import ModalProvider from 'providers/ModalProvider';
import ThemeProvider from 'providers/ThemeProvider';
import TopbarProvider from 'providers/TopbarProvider';
import UserProvider from 'providers/UserProvider';
import UtmProvider from 'providers/UtmProvider';

import SEO from '../../next-seo.config';

import APP from 'constants/app';
import PERMISSIONS from 'constants/permissions';

const WEB_VITAL_METRIC_LABEL = 'web-vital';

const queryConfig = {
  queries: {
    // By default we enable automatic queries on component mount,
    // change this when using queries based in event listeners
    enabled: true,
    // 4 minutes of queries in memory is enough
    // See: https://react-query.tanstack.com/docs/guides/caching
    cacheTime: 4 * 60 * 1000,
    // 30 seconds for caching without re-fetching on the backend
    // See: https://react-query.tanstack.com/docs/guides/caching
    staleTime: 0.5 * 60 * 1000,
    queryFn: get,
    // Most of our features probably won't require refetch on windows focus
    // If it turns out to be the case though, change the default behavior here
    refetchOnWindowFocus: false,
    // All of our requests uses the same backend, and it's very reliable
    // If a request failed, there is no need to retry
    // It probably REALLY failed and won't recover by itself
    retry: false,
  },
};

export function reportWebVitals(metric) {
  if (metric.label === WEB_VITAL_METRIC_LABEL) {
    sendWebVitalsToGoogleAnalytics(metric);
  }
}

const PromobitApp = ({ Component, pageProps }) => {
  const router = useRouter();
  const { asPath } = router;
  const [currUrl, setCurrUrl] = useState(asPath);
  const [isOnboardingShowed, setIsOnboardingShowed] = useState(
    isNotificationOnboardingShowed
  );

  useEffect(() => {
    if ('serviceWorker' in navigator && 'Notification' in window) {
      const requestNotificationPermission = async () => {
        const permission = await Notification.requestPermission();

        if (permission === PERMISSIONS.GRANTED) {
          await initializeFirebaseApp();
          setIsOnboardingShowed(true);
        }
      };

      if (
        !isOnboardingShowed &&
        Notification.permission !== PERMISSIONS.DENIED
      ) {
        window.addEventListener('click', requestNotificationPermission, {
          once: true,
        });

        return () => {
          window.removeEventListener('click', requestNotificationPermission);
        };
      }
      if (Notification.permission === PERMISSIONS.DENIED) {
        clearStoragesWebPushPermissionEntries();
      }
    }
  }, [isOnboardingShowed]);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const cachedScrollPositions = [];
    let shouldScrollRestore = false;

    const restoreScroll = () => {
      if (!shouldScrollRestore) {
        return;
      }

      const { x, y } = shouldScrollRestore;
      window.scrollTo(x, y);
      shouldScrollRestore = false;
    };

    const cacheScrollPosition = () => {
      cachedScrollPositions.push([window.scrollX, window.scrollY]);
    };

    router.events.on('routeChangeStart', cacheScrollPosition);
    router.events.on('routeChangeComplete', restoreScroll);

    router.beforePopState(() => {
      if (cachedScrollPositions.length > 0) {
        const [x, y] = cachedScrollPositions.pop();
        shouldScrollRestore = { x, y };
      }

      return true;
    });

    return () => {
      router.events.off('routeChangeStart', cacheScrollPosition);
      router.events.off('routeChangeComplete', restoreScroll);
    };
  }, []);

  useEffect(() => {
    const handleRouteChange = (nextUrl) => {
      if (nextUrl !== currUrl) {
        sendPageView(nextUrl);
      }

      setCurrUrl(nextUrl);
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [currUrl]);

  return (
    <>
      <DefaultSeo {...SEO} canonical={`${APP.URL}${router.asPath}`} />
      <MouseflowScriptTag />
      <GoogleTagManager />
      <ThemeProvider>
        <GlobalStyles />
        <ShopperGlobalStyles />
        <LocalWishlistProvider>
          <ModalProvider>
            <TopbarProvider
              categories={pageProps[APP.CTX_OPTIONAL_PROPS]?.categories}
              featuredLanding={
                pageProps[APP.CTX_OPTIONAL_PROPS]?.featuredLanding
              }
              isTopbarActive={pageProps[APP.CTX_OPTIONAL_PROPS]?.isTopbarActive}
              highlightedLandings={
                pageProps[APP.CTX_OPTIONAL_PROPS]?.highlightedLandings
              }
              stores={pageProps[APP.CTX_OPTIONAL_PROPS]?.stores}
              topbarAd={pageProps[APP.CTX_OPTIONAL_PROPS]?.topbarAd}
            >
              <DrawerProvider>
                <UserProvider
                  ssrAuthToken={pageProps[APP.CTX_OPTIONAL_PROPS]?.ssrAuthToken}
                >
                  <UtmProvider>
                    <ReactQueryConfigProvider config={queryConfig}>
                      <UniversalSchemas />
                      <ModalRoot />
                      <DrawerRoot />
                      <Topbar />
                      <WishlistModalOnUrlHash />
                      <Component {...pageProps} />
                      <LGPDDialogBox />
                      <DownloadAppCTA />
                      <ToastRoot />
                    </ReactQueryConfigProvider>
                  </UtmProvider>
                </UserProvider>
              </DrawerProvider>
            </TopbarProvider>
          </ModalProvider>
        </LocalWishlistProvider>
      </ThemeProvider>
      {parseToBool(process.env.NEXT_PUBLIC_REACT_QUERY_DEVTOOLS) && (
        <ReactQueryDevtools
          panelProps={{
            style: {
              height: `${process.env.NEXT_PUBLIC_REACT_QUERY_DEVTOOLS_HEIGHT}px`,
            },
          }}
        />
      )}
    </>
  );
};

PromobitApp.defaultProps = {
  pageProps: {
    categories: [],
    stores: [],
    topbarAd: null,
  },
};

PromobitApp.propTypes = {
  pageProps: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    categories: PropTypes.array,
    // eslint-disable-next-line react/forbid-prop-types
    stores: PropTypes.array,
    topbarAd: PropTypes.shape({
      imageSrc: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
      isClosable: PropTypes.bool.isRequired,
      bgColor: PropTypes.string.isRequired,
    }),
  }),
};

export default PromobitApp;
